<template>
  <div class="item-inventories">
    <Breadcrumbs v-bind:breadcrumbs="breadcrumbs" />

    <DataTable
      class="p-datatable-sm p-datatable-striped p-datatable-gridlines"
      :value="getItemInventories"
      :loading="isLoading"
      :rowHover="true"
      :lazy="true"
      filterDisplay="row"
      :stateKey="tableStateStore.filterName()"
      :filters.sync="tableStateStore.filters"
      :rows.sync="tableStateStore.pagination.pageSize"
      :sortField="tableStateStore.sorting.sortField"
      :sortOrder="tableStateStore.sorting.sortOrder"
      :first="tableStateStore.skip()"
      stateStorage="local"
      :totalRecords="getItemInventoryCount"
      :paginator="true"
      paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
      :currentPageReportTemplate="
        $t('show') +
          ' {first} ' +
          $t('to') +
          ' {last} ' +
          $t('of') +
          ' {totalRecords} ' +
          $t('records')
      "
      :rowsPerPageOptions="[10, 20, 50, 100]"
      @page="onPage($event)"
      @sort="onSort($event)"
      @filter="onFilter($event)"
      @state-restore="onStateRestore($event)"
      responsiveLayout="scroll"
    >
      <template #header>
        <div class="table-header">
          <div class="table-header-left">
            <h1 class="page-title">{{ $t('item_inventories') }}</h1>
          </div>
          <div class="table-header-right">
            <button class="btn btn-sm btn-inverse" @click.prevent="onRemoveFilters()">
              <i class="fa fa-remove mr-2"></i>
              {{ $t('remove_filters') }}
            </button>

            <ButtonWait
              :startCallback="startItemInventoriesExport"
              :pollCallback="pollExportBuildState"
              class="pull-right"
            >
              <template v-slot:buttonText>
                {{ $t('export_view') }}
              </template>
            </ButtonWait>
          </div>
        </div>
      </template>
      <template #empty>
        {{ $t('no_data_found') }}
      </template>
      <template #loading>
        {{ $t('loading') }}
      </template>

      <Column
        field="item.number"
        :header="$t('item_number')"
        sortable
        :headerStyle="{ width: '40px' }"
        :bodyStyle="'text-align: center;width: 40px'"
        filterField="itemNumber"
      >
        <template #body="slotProps">
          <span v-if="slotProps.data && slotProps.data.item && slotProps.data.item.number !== null">
            {{ slotProps.data.item.number.toString().padStart(5, '0') }}
          </span>
        </template>
        <template #filter="{filterModel,filterCallback}">
          <InputText type="text" v-model="filterModel.value" @input="filterCallback()" />
        </template>
      </Column>

      <Column
        field="item.description"
        :header="$t('item_description')"
        sortable
        filterMatchMode="contains"
        filterField="itemDescription"
      >
        <template #body="slotProps">
          <span
            v-if="slotProps.data && slotProps.data.item && slotProps.data.item.description !== null"
          >
            {{ slotProps.data.item.description }}
          </span>
        </template>
        <template #filter="{filterModel,filterCallback}">
          <InputText type="text" v-model="filterModel.value" @input="filterCallback()" />
        </template>
      </Column>

      <Column
        field="count"
        :header="$t('count')"
        sortable
        :headerStyle="{ width: '100px' }"
        :bodyStyle="'text-align: center;width: 100px'"
      >
        <template #body="slotProps">
          <span v-if="slotProps.data && slotProps.data.count !== null">
            {{ slotProps.data.count }}
          </span>
        </template>
        <template #filter="{filterModel,filterCallback}">
          <InputText
            type="text"
            v-model="filterModel.value"
            @input="filterCallback()"
            placeholder=">="
          />
        </template>
      </Column>

      <Column
        field="location"
        :header="$t('location')"
        sortable
        filterMatchMode="contains"
        filterField="locationName"
      >
        <template #body="slotProps">
          <div v-if="slotProps.data">
            <span v-if="slotProps.data.project">
              <i class="pi pi-home mr-2"></i>
              #{{ slotProps.data.project.number }} {{ slotProps.data.project.customer.firstname }}
              {{ slotProps.data.project.customer.lastname }}
            </span>
            <span v-if="slotProps.data.storage">
              <i
                v-if="slotProps.data.storage.itemStorageType === 'STATIONARY'"
                class="pi pi-box mr-2"
              ></i>
              <i
                v-if="slotProps.data.storage.itemStorageType === 'VEHICLE'"
                class="pi pi-car mr-2"
              ></i>
              #{{ slotProps.data.storage.number }} {{ slotProps.data.storage.name }}
            </span>
          </div>
        </template>
        <template #filter="{filterModel,filterCallback}">
          <InputText type="text" v-model="filterModel.value" @input="filterCallback()" />
        </template>
      </Column>
    </DataTable>
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';
import Breadcrumbs from '@/components/Breadcrumbs';
import DataTable from 'primevue/datatable';
import Column from 'primevue/column';
import InputText from 'primevue/inputtext';
import ButtonWait from '@/components/ButtonWait.vue';

export default {
  name: 'ItemInventoriesPage',
  components: {
    Breadcrumbs,
    DataTable,
    InputText,
    Column,
    ButtonWait,
  },
  data() {
    return {
      itemInventoryTableStateConfig: {
        name: 'itemInventories',
        filters: {
          itemNumber: { value: null },
          itemDescription: { value: null },
          locationName: { value: null },
          count: { value: null },
        },
        customFilters: {},
        sorting: { sortField: 'item.number', sortOrder: 1 },
      },
    };
  },
  computed: {
    ...mapGetters(['getItemInventories', 'getItemInventoryCount', 'isLoading', 'getTableState']),
    ...mapGetters('auth', ['getCurrentUser']),
    breadcrumbs() {
      return [{ name: 'Home', route: { name: 'home' } }, { name: this.$t('item_inventories') }];
    },
    tableStateStore() {
      return this.getTableState(this.itemInventoryTableStateConfig.name);
    },
  },
  methods: {
    ...mapActions([
      'fetchItemInventoriesPaginated',
      'initTableState',
      'itemInventoriesExport',
      'fetchUserAttachmentUrl',
    ]),
    ...mapActions('auth', ['refreshUser']),

    /**
     * Load remote table data
     */
    async loadTableData() {
      await this.fetchItemInventoriesPaginated({
        pagination: {
          skip: this.tableStateStore.skip(),
          pageSize: this.tableStateStore.pagination.pageSize,
        },
        sorting: this.tableStateStore.sorting,
        filters: { ...this.tableStateStore.filters, ...this.tableStateStore.customFilters },
      });
    },

    /**
     * Load more results from server or cache on pagination click
     */
    async onPage(event) {
      this.tableStateStore.pagination = { page: event.page, pageSize: event.rows };
      this.loadTableData();
    },

    /**
     * Load results from server/cache on sort
     */
    async onSort(event) {
      this.tableStateStore.sorting = { sortField: event.sortField, sortOrder: event.sortOrder };
      this.loadTableData();
    },

    /**
     * Load results from server/cache on filter
     */
    async onFilter(event) {
      this.loadTableData();
    },

    /**
     * Remove all filters
     * @param {*} event
     */
    async onRemoveFilters(event) {
      this.tableStateStore.reset();
      await this.loadTableData();
    },

    /**
     * Trigger when state is loaded from local storage
     */
    async onStateRestore(event) {
      this.tableStateStore.restore(event);
    },

    async startItemInventoriesExport() {
      if (!this.getCurrentUser) return;

      await this.itemInventoriesExport({
        userId: this.getCurrentUser.id,
        pagination: {
          skip: this.tableStateStore.skip(),
          pageSize: this.tableStateStore.pagination.pageSize,
        },
        sorting: this.tableStateStore.sorting,
        filters: { ...this.tableStateStore.filters, ...this.tableStateStore.customFilters },
      });
    },

    async pollExportBuildState() {
      await this.refreshUser();

      const buildState = this.getCurrentUser?.itemInventoriesExportBuildState;
      if (buildState === 'FINISHED') {
        await this.downloadItemInventoriesExport();
      }
      return buildState;
    },

    async downloadItemInventoriesExport() {
      if (!this.getCurrentUser) return;
      const url = await this.fetchUserAttachmentUrl({
        userId: this.getCurrentUser.id,
        attachmentType: 'itemInventoriesExportAttachment',
      });
      window.open(url);
    },
  },
  async created() {
    await this.initTableState(this.itemInventoryTableStateConfig);
  },
  async mounted() {
    await this.loadTableData();
  },
};
</script>

<style scoped lang="scss">
::v-deep .p-column-filter-clear-button {
  display: none;
  width: 0;
}
::v-deep .p-column-filter-menu-button {
  display: none;
  width: 0;
}
/** Fix Dropdown overlay */
::v-deep .p-datatable-responsive-scroll > .p-datatable-wrapper {
  min-height: 75vh;
}
</style>
